import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Navigation, MapPin, Car, Square, Loader2, Clock, Route, X, ChevronDown, ChevronUp } from 'lucide-react';
import { format } from 'date-fns';

// Haversine formula to calculate distance between two GPS points
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3958.8; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Reverse geocode to get address from coordinates
const getAddressFromCoords = async (lat, lon) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=18&addressdetails=1`,
      { headers: { 'Accept-Language': 'en' } }
    );
    const data = await response.json();
    if (data.address) {
      const { house_number, road, city, town, village, state } = data.address;
      const street = house_number ? `${house_number} ${road}` : road;
      const locality = city || town || village || '';
      return `${street || ''}, ${locality}${state ? `, ${state}` : ''}`.replace(/^, |, $/g, '');
    }
    return `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
  } catch (err) {
    return `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
  }
};

export default function MileageTracker({ onExpenseAdded }) {
  const [isTracking, setIsTracking] = useState(false);
  const [totalMiles, setTotalMiles] = useState(0);
  const [startLocation, setStartLocation] = useState(null);
  const [startAddress, setStartAddress] = useState('');
  const [currentAddress, setCurrentAddress] = useState('');
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [mileageRate, setMileageRate] = useState(0.70);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [tripNotes, setTripNotes] = useState('');
  const [editableMiles, setEditableMiles] = useState(0);
  const [editableStartAddress, setEditableStartAddress] = useState('');
  const [editableEndAddress, setEditableEndAddress] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [gpsError, setGpsError] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  const watchIdRef = useRef(null);
  const lastPositionRef = useRef(null);
  const timerRef = useRef(null);

  // Fetch mileage rate from settings
  useEffect(() => {
    const fetchRate = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const response = await axios.get(`${API}/settings/mileage`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data && response.data.rate) {
          setMileageRate(response.data.rate);
        }
      } catch (err) {
        console.log('Using default mileage rate');
      }
    };
    fetchRate();
  }, []);

  // Timer for elapsed time
  useEffect(() => {
    if (isTracking && startTime) {
      timerRef.current = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isTracking, startTime]);

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const startTracking = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setGpsError(null);
    setIsLoading(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setStartLocation({ lat: latitude, lon: longitude });
        lastPositionRef.current = { lat: latitude, lon: longitude };
        
        const address = await getAddressFromCoords(latitude, longitude);
        setStartAddress(address);
        setCurrentAddress(address);
        
        setStartTime(Date.now());
        setIsTracking(true);
        setTotalMiles(0);
        setElapsedTime(0);
        setIsLoading(false);

        watchIdRef.current = navigator.geolocation.watchPosition(
          async (pos) => {
            const { latitude: lat, longitude: lon, accuracy } = pos.coords;
            if (accuracy > 50) return;
            
            if (lastPositionRef.current) {
              const distance = calculateDistance(
                lastPositionRef.current.lat,
                lastPositionRef.current.lon,
                lat,
                lon
              );
              
              if (distance > 0.01) {
                setTotalMiles(prev => prev + distance);
                lastPositionRef.current = { lat, lon };
                const addr = await getAddressFromCoords(lat, lon);
                setCurrentAddress(addr);
              }
            }
          },
          (err) => {
            console.error('GPS error:', err);
            setGpsError('GPS signal lost. Please ensure location services are enabled.');
          },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
        );

        toast.success('Mileage tracking started!');
      },
      (err) => {
        setIsLoading(false);
        let errorMsg = 'Unable to get your location.';
        if (err.code === 1) {
          errorMsg = 'Location permission denied. Please enable location access.';
        } else if (err.code === 2) {
          errorMsg = 'Location unavailable. Please try again.';
        } else if (err.code === 3) {
          errorMsg = 'Location request timed out. Please try again.';
        }
        setGpsError(errorMsg);
        toast.error(errorMsg);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const stopTracking = () => {
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setEditableMiles(totalMiles);
    setEditableStartAddress(startAddress);
    setEditableEndAddress(currentAddress);
    setShowConfirmDialog(true);
  };

  const cancelTrip = () => {
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setIsTracking(false);
    setTotalMiles(0);
    setStartLocation(null);
    setStartAddress('');
    setCurrentAddress('');
    setStartTime(null);
    setElapsedTime(0);
    setTripNotes('');
    toast.info('Trip cancelled');
  };

  const saveTrip = async () => {
    setIsLoading(true);
    
    try {
      const milesToSave = editableMiles || totalMiles;
      const fromAddr = editableStartAddress || startAddress || 'Start';
      const toAddr = editableEndAddress || currentAddress || 'End';
      const amount = (milesToSave * mileageRate).toFixed(2);
      const description = `Mileage: ${fromAddr} to ${toAddr} (${milesToSave.toFixed(2)} miles @ $${mileageRate}/mi)${tripNotes ? ' - ' + tripNotes : ''}`;
      
      // Use startTime if available, otherwise use current date
      const tripDate = startTime ? new Date(startTime) : new Date();
      
      const expenseData = {
        date: format(tripDate, 'yyyy-MM-dd'),
        vendor: 'Mileage',
        category: 'Car & Truck Expenses',
        amount: parseFloat(amount),
        notes: description
      };
      
      console.log('Saving expense:', expenseData);

      const token = localStorage.getItem('token');
      await axios.post(`${API}/expenses`, expenseData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Trip recorded: ${milesToSave.toFixed(2)} miles = $${amount}`);
      
      setIsTracking(false);
      setShowConfirmDialog(false);
      setTotalMiles(0);
      setEditableMiles(0);
      setEditableStartAddress('');
      setEditableEndAddress('');
      setStartLocation(null);
      setStartAddress('');
      setCurrentAddress('');
      setStartTime(null);
      setElapsedTime(0);
      setTripNotes('');
      
      if (onExpenseAdded) {
        onExpenseAdded();
      }
    } catch (err) {
      console.error('Failed to save trip:', err);
      const errorMsg = err.response?.data?.detail;
      if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else if (Array.isArray(errorMsg)) {
        toast.error(errorMsg.map(e => e.msg || e.message).join(', '));
      } else {
        toast.error('Failed to save trip. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const displayMiles = showConfirmDialog ? editableMiles : totalMiles;
  const estimatedAmount = (displayMiles * mileageRate).toFixed(2);

  return (
    <>
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 shadow-lg">
        <CardContent className="p-4">
          {/* Collapsible Header */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="w-full flex items-center justify-between mb-3"
          >
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full flex items-center justify-center">
                <Car className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-blue-800">Track Mileage</span>
              {isTracking && (
                <div className="flex items-center gap-1 ml-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <span className="text-xs text-green-600 font-medium">Active</span>
                </div>
              )}
            </div>
            {isCollapsed ? (
              <ChevronDown className="w-5 h-5 text-blue-600" />
            ) : (
              <ChevronUp className="w-5 h-5 text-blue-600" />
            )}
          </button>

          {/* Collapsible Content */}
          {!isCollapsed && (
            <>
              {!isTracking ? (
                <div className="text-center">
                  <p className="text-sm text-blue-700 mb-4">
                    Track your drive and auto-record as expense
                  </p>
                  <Button
                    onClick={startTracking}
                    disabled={isLoading}
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3 rounded-xl shadow-md"
                  >
                    {isLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <Navigation className="w-5 h-5 mr-2" />
                    )}
                    {isLoading ? 'Getting Location...' : 'Start Tracking'}
                  </Button>
                  {gpsError && (
                    <p className="text-xs text-red-600 mt-2">{gpsError}</p>
                  )}
                  <p className="text-xs text-blue-600 mt-2">
                    Rate: ${mileageRate}/mile
                  </p>
                </div>
              ) : (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                  <span className="font-semibold text-green-700">Tracking Active</span>
                </div>
                <div className="flex items-center gap-1 text-slate-600">
                  <Clock className="w-4 h-4" />
                  <span className="font-mono text-sm">{formatTime(elapsedTime)}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-white/70 rounded-xl p-3 text-center">
                  <Route className="w-5 h-5 mx-auto text-blue-600 mb-1" />
                  <div className="text-2xl font-bold text-slate-800">{totalMiles.toFixed(2)}</div>
                  <div className="text-xs text-slate-600">Miles</div>
                </div>
                <div className="bg-white/70 rounded-xl p-3 text-center">
                  <Car className="w-5 h-5 mx-auto text-green-600 mb-1" />
                  <div className="text-2xl font-bold text-green-700">${estimatedAmount}</div>
                  <div className="text-xs text-slate-600">Est. Amount</div>
                </div>
              </div>

              <div className="space-y-2 mb-4 text-sm">
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-xs text-slate-500">From</div>
                    <div className="text-slate-700 truncate">{startAddress || 'Getting location...'}</div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-xs text-slate-500">Current</div>
                    <div className="text-slate-700 truncate">{currentAddress || 'Tracking...'}</div>
                  </div>
                </div>
              </div>

              {gpsError && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3">
                  <p className="text-xs text-amber-700">{gpsError}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={cancelTrip}
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50"
                >
                  <X className="w-4 h-4 mr-1" />
                  Cancel
                </Button>
                <Button
                  onClick={stopTracking}
                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
                >
                  <Square className="w-4 h-4 mr-1" />
                  Arrived
                </Button>
              </div>
            </div>
          )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Car className="w-5 h-5 text-blue-600" />
              Save Trip as Expense
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-600">Distance:</span>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editableMiles}
                    onChange={(e) => setEditableMiles(parseFloat(e.target.value) || 0)}
                    className="w-24 h-8 text-right font-semibold"
                  />
                  <span className="text-slate-600">miles</span>
                </div>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Rate:</span>
                <span className="font-semibold">${mileageRate}/mile</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Duration:</span>
                <span className="font-semibold">{formatTime(elapsedTime)}</span>
              </div>
              <div className="border-t pt-2 flex justify-between">
                <span className="text-slate-600">Total Amount:</span>
                <span className="text-lg font-bold text-green-600">${estimatedAmount}</span>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <Label className="text-xs text-slate-500 flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-green-600" />
                  From
                </Label>
                <Input
                  value={editableStartAddress}
                  onChange={(e) => setEditableStartAddress(e.target.value)}
                  className="mt-1 text-sm"
                  placeholder="Start address"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500 flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-red-600" />
                  To
                </Label>
                <Input
                  value={editableEndAddress}
                  onChange={(e) => setEditableEndAddress(e.target.value)}
                  className="mt-1 text-sm"
                  placeholder="End address"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="trip-notes" className="text-sm">Trip Purpose / Notes (Optional)</Label>
              <Textarea
                id="trip-notes"
                value={tripNotes}
                onChange={(e) => setTripNotes(e.target.value)}
                placeholder="e.g., Client meeting at ABC Corp"
                className="mt-1"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowConfirmDialog(false);
                // Resume tracking
                if (startLocation) {
                  watchIdRef.current = navigator.geolocation.watchPosition(
                    async (pos) => {
                      const { latitude: lat, longitude: lon, accuracy } = pos.coords;
                      if (accuracy > 50) return;
                      if (lastPositionRef.current) {
                        const distance = calculateDistance(
                          lastPositionRef.current.lat,
                          lastPositionRef.current.lon,
                          lat,
                          lon
                        );
                        if (distance > 0.01) {
                          setTotalMiles(prev => prev + distance);
                          lastPositionRef.current = { lat, lon };
                          const addr = await getAddressFromCoords(lat, lon);
                          setCurrentAddress(addr);
                        }
                      }
                    },
                    (err) => console.error('GPS error:', err),
                    { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
                  );
                }
              }}
            >
              Continue Trip
            </Button>
            <Button
              onClick={saveTrip}
              disabled={isLoading}
              className="bg-green-600 hover:bg-green-700"
            >
              {isLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Save Expense
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
